import { memo, useCallback, useRef, useState } from 'react';
import type { BookingDTO, CustomerDTO } from '@six-pockets/shared';
import { PauseIcon, PlayIcon, PlusIcon, RestartIcon, StopIcon, UsersIcon } from '@/components/icons';
import { Button, StatusDot } from '@/components/ui';
import { useTableActions } from '@/context/TablesContext';
import { useNow } from '@/hooks/useNow';
import { cx } from '@/utils/cx';
import { firstName, formatClock, formatDuration, formatINR, formatShortDuration } from '@/utils/format';
import { elapsedMs, isBillable, isLive } from '@/utils/booking';
import type { BookingAction } from '@/api';
import { bookingDotStatus } from '../status';
import { BookingNamesEditor } from './BookingNamesEditor';
import { NameInput } from './NameInput';
import styles from '@/styles/components/BookingRow/style.scss';

interface BookingRowProps {
  tableId: string;
  booking: BookingDTO;
  selected: boolean;
}

function selectHint(booking: BookingDTO) {
  if (booking.billStatus === 'billed') return 'Already billed';
  if (booking.status === 'completed') return 'Select for billing';
  return 'End this booking to bill it';
}

/**
 * The running play time. Its own component so the once-a-second tick re-renders this text only, not the row
 * (name input, buttons). Paused bookings don't tick.
 */
const LiveTimer = memo(function LiveTimer({ booking, className }: { booking: BookingDTO; className: string }) {
  const now = useNow(booking.status === 'active' ? 1000 : null);
  return <span className={className}>{formatDuration(elapsedMs(booking, now))}</span>;
});

/**
 * One booking on a card. Memoised: it re-renders only when this booking (kept as the same object while it is
 * unchanged, see TablesContext) or its checkbox changes.
 */
export const BookingRow = memo(function BookingRow({ tableId, booking, selected }: BookingRowProps) {
  const { bookingAction, renameBooking, setBookingCustomer, setSecondName, toggleSelected } = useTableActions();
  const live = isLive(booking.status);
  const status = bookingDotStatus(booking);
  const who = booking.customerName || 'unnamed booking';
  const id = booking.id;

  // The action waiting for the server. Until it answers, the row's controls are disabled (greyed out), so an action
  // can't be sent twice or overlap another (Stop while a Pause is still on its way).
  const [pending, setPending] = useState<BookingAction | null>(null);
  const act = (action: BookingAction) => async () => {
    if (pending) return;
    setPending(action);
    try {
      await bookingAction(id, action);
    } finally {
      setPending(null);
    }
  };
  const waiting = pending !== null;
  const toggle = useCallback(() => toggleSelected(tableId, id), [toggleSelected, tableId, id]);
  const rename = useCallback((name: string) => void renameBooking(id, name), [renameBooking, id]);
  const pickCustomer = useCallback((c: CustomerDTO) => void setBookingCustomer(id, c.id), [setBookingCustomer, id]);
  // Doubles: a second name (the other team). Both are edited in the names editor, opened from the row's
  // "Kiran & Vikram" or its ＋ ('second' focuses the second name). An emptied second name is removed.
  const [namesOpen, setNamesOpen] = useState<'first' | 'second' | null>(null);
  const namesRef = useRef<HTMLDivElement>(null);
  const closeNames = useCallback(() => setNamesOpen(null), []);
  const renameSecond = useCallback(
    (name: string) => {
      if (name || booking.customerName2) void setSecondName(id, { customerName2: name });
    },
    [setSecondName, id, booking.customerName2],
  );
  const pickSecond = useCallback((c: CustomerDTO) => void setSecondName(id, { customerId2: c.id }), [setSecondName, id]);
  const twoNames = !!booking.customerName2;
  const bothCustomers = booking.customerId !== null && booking.customerId2 !== null;
  const fullNames = [booking.customerName, booking.customerName2].filter(Boolean).join(' & ');

  return (
    <li className={cx(styles.row, live && styles.live, selected && styles.selected)}>
      <input
        type="checkbox"
        checked={selected}
        disabled={!isBillable(booking)}
        onChange={toggle}
        aria-label={`Select ${who} for billing`}
        title={selectHint(booking)}
        className={styles.check}
      />
      <StatusDot tone={status.tone} label={status.label} className={styles.dot} />
      <div ref={namesRef} className={cx(styles.name, styles.names)}>
        {twoNames ? (
          // Two names: first names only ("Kiran & Vikram"), the customer mark when both are existing customers. A tap
          // opens both full names to edit.
          <button
            type="button"
            className={styles.namesButton}
            onClick={() => setNamesOpen((open) => (open ? null : 'first'))}
            aria-expanded={namesOpen !== null}
            aria-label={`Names: ${fullNames}. Edit`}
            title={fullNames}
          >
            <span className={styles.namesText}>
              {firstName(booking.customerName) || 'No name'} &amp; {firstName(booking.customerName2)}
            </span>
            {bothCustomers && (
              <span className={styles.namesMark} title="Existing customers">
                <UsersIcon size={13} />
              </span>
            )}
          </button>
        ) : (
          <>
            <NameInput
              value={booking.customerName}
              linked={booking.customerId !== null}
              label={`Customer name for the ${formatClock(booking.startedAt)} booking`}
              onCommit={rename}
              onPick={pickCustomer}
              className={styles.nameBox}
              // Doubles: a second payer (the other team); billing then opens one bill per name. Inside the box before
              // the customer mark, so the marks line up with the two-name rows'.
              action={
                booking.billStatus === 'unbilled' ? (
                  <button
                    type="button"
                    className={styles.addName}
                    onClick={() => setNamesOpen('second')}
                    aria-label={`Add a second name to the ${formatClock(booking.startedAt)} booking`}
                    title="Add a second name (doubles: each team gets its own bill)"
                  >
                    <PlusIcon size={13} />
                  </button>
                ) : undefined
              }
            />
          </>
        )}
      </div>
      {namesOpen && (
        <BookingNamesEditor
          booking={booking}
          anchorRef={namesRef}
          focusSecond={namesOpen === 'second'}
          onRename={rename}
          onPick={pickCustomer}
          onRenameSecond={renameSecond}
          onPickSecond={pickSecond}
          onClose={closeNames}
        />
      )}
      <span className={cx(styles.time, styles.start)}>{formatClock(booking.startedAt)}</span>
      <span className={cx(styles.time, styles.end)}>{booking.endedAt ? formatClock(booking.endedAt) : 'Open'}</span>
      {/* The live play timer; once ended, the charged time (start to end, pauses included). */}
      {live ? (
        <LiveTimer booking={booking} className={cx(styles.duration, booking.status === 'paused' && styles.pausedText)} />
      ) : (
        <span className={cx(styles.duration, styles.endedText)}>
          {booking.endedAt ? formatShortDuration(new Date(booking.endedAt).getTime() - new Date(booking.startedAt).getTime()) : ''}
        </span>
      )}
      {/* One last column: the actions while the booking runs (no amount yet), the amount once it has ended. */}
      {live ? (
        <div className={styles.actions}>
          {booking.status === 'paused' ? (
            <Button size="sm" variant="success" iconOnly icon={<PlayIcon size={13} />} aria-label="Resume booking" title="Resume" disabled={waiting} aria-busy={pending === 'resume'} onClick={act('resume')} />
          ) : (
            <Button size="sm" variant="warning" iconOnly icon={<PauseIcon size={13} />} aria-label="Pause booking" title="Pause" disabled={waiting} aria-busy={pending === 'pause'} onClick={act('pause')} />
          )}
          <Button size="sm" variant="danger" iconOnly icon={<StopIcon size={13} />} aria-label="Stop booking" title="Stop" disabled={waiting} aria-busy={pending === 'stop'} onClick={act('stop')} />
          <Button size="sm" iconOnly icon={<RestartIcon size={16} />} aria-label="Restart booking" title="Restart: end this booking and start a new one" disabled={waiting} aria-busy={pending === 'restart'} onClick={act('restart')} />
        </div>
      ) : (
        <span className={styles.amount}>{booking.amount !== null ? formatINR(booking.amount) : ''}</span>
      )}
    </li>
  );
});
